import React, { useState, useEffect, useRef } from 'react';
import {
  TrashIcon,
  ClockIcon,
  EyeSlashIcon,
  XCircleIcon,
  InformationCircleIcon,
  LanguageIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { BoxArrowUpRight, Send } from 'react-bootstrap-icons';
import { EyeIcon } from '@heroicons/react/24/outline';
import CommentReplyPane from './CommentReplyPane.tsx';
import { formatDate } from '../../utils/formatDate.tsx';
import { Social } from '../../context/socialProvider.tsx';
import { useLocation } from 'react-router-dom';
import UnifiedCommentDto from '../../types/comment.types.ts';
import { translateComment, TranslatedCommentDto } from '../../api/comment.ts';
import {
  LanguageDisplayNames,
  SupportedLanguage,
} from '../../types/language.typtes.ts';

function CaptionWithInlineMore({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (spanRef.current) {
      setIsTruncated(
        spanRef.current.scrollHeight > spanRef.current.clientHeight + 1
      );
    }
  }, [text]);

  if (!text) return null;

  return (
    <div className="relative" style={{ height: '3rem' }}>
      {/* Collapsed (clamped) */}
      {!expanded && (
        <>
          <span
            ref={spanRef}
            className="text-sm transition-all duration-300 line-clamp-2"
            style={{
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              verticalAlign: 'middle',
              wordBreak: 'break-word',
            }}
          >
            {text}
          </span>
          {isTruncated && (
            <span
              className="absolute right-0 bottom-0 text-blue-500 underline cursor-pointer text-sm bg-black bg-opacity-80 px-1"
              style={{
                fontSize: 'inherit',
                verticalAlign: 'baseline',
              }}
              onClick={() => setExpanded(true)}
              tabIndex={0}
              role="button"
            >
              More
            </span>
          )}
        </>
      )}
      {/* Expanded (overflowing, absolute, black bg) */}
      {expanded && (
        <span
          className="absolute left-0 top-0 w-full text-sm px-3 py-2 rounded-lg z-10 backdrop-blur-sm"
          style={{
            background: 'rgba(0,0,0,0.50)',
            color: 'white',
            fontSize: 'inherit',
            minHeight: '3rem',
            boxSizing: 'border-box',
          }}
        >
          {text}
          <span
            className="text-blue-500 underline cursor-pointer ml-2"
            style={{
              fontSize: 'inherit',
              verticalAlign: 'baseline',
            }}
            onClick={() => setExpanded(false)}
            tabIndex={0}
            role="button"
          >
            Less
          </span>
        </span>
      )}
    </div>
  );
}

const DetailedPane = ({
  currentSocial,
  comments,
  setComments,
  selectedComment,
  setSelectedComment,
  deleteWindow,
  isLoading,
  updateCommentHiddenStatus,
  replyToComment,
  autoMod = false,
}: {
  currentSocial: any;
  comments: any;
  setComments: any;
  selectedComment: any;
  setSelectedComment: any;
  deleteWindow: any;
  isLoading: any;
  updateCommentHiddenStatus: any;
  replyToComment: any;
  autoMod?: boolean;
}) => {
  if (selectedComment === undefined || !comments[selectedComment]) {
    return (
      <div className="flex items-center justify-center h-full w-full absolute inset-0">
        <p className="text-sm text-gray-500">Select a comment to get started</p>
      </div>
    );
  }

  const [replyToggle, setReplyToggle] = useState(false);
  const [showCaptionOverlay, setShowCaptionOverlay] = useState(false);
  const [isCaptionLong, setIsCaptionLong] = useState(false);
  const captionRef = useRef<HTMLParagraphElement>(null);

  const [isPostMessageExpanded, setIsPostMessageExpanded] = useState(false);
  // Add ref to check post message overflow
  const postMessageRef = useRef<HTMLParagraphElement>(null);
  // Add state to track if post message is overflowing
  const [isPostMessageOverflowing, setIsPostMessageOverflowing] =
    useState(false);

  const selected = comments[selectedComment];
  const isCommentHidden = selected.hidden == true;
  const mediaUrl = comments[selectedComment].post?.mediaUrl || '';

  const location = useLocation();
  const linkToPost =
    location.pathname === '/facebookAdComments'
      ? `https://www.facebook.com/${comments[selectedComment].postId}`
      : comments[selectedComment].post.permalinkUrl;

  useEffect(() => {
    setReplyToggle(false);
    // Reset expanded state when selected comment changes
    setIsPostMessageExpanded(false);

    // Check if post message overflows in the next render
    setTimeout(() => {
      if (postMessageRef.current) {
        setIsPostMessageOverflowing(
          postMessageRef.current.scrollHeight >
            postMessageRef.current.clientHeight
        );
      }
    }, 0);
  }, [selected]);

  useEffect(() => {
    if (captionRef.current) {
      setIsCaptionLong(
        captionRef.current.scrollHeight > captionRef.current.clientHeight
      );
    }
  }, [selectedComment, comments]);

  return (
    <div className="flex flex-col h-full">
      <div className="pb-2 border-b border-opacity-20 border-white flex flex-col items-center p-2">
        {mediaUrl ? (
          <div className="w-full">
            <a
              href={linkToPost}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full"
            >
              <div className="relative overflow-hidden rounded group">
                <img
                  src={mediaUrl}
                  className="object-cover h-32 w-full bg-white bg-opacity-10 mb-1 transform transition-all duration-300 group-hover:scale-105"
                  alt="Post media"
                />
                <div className="absolute inset-0 transition-all duration-300 cursor-pointer bg-black opacity-0 group-hover:opacity-30" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                  <BoxArrowUpRight className="text-white" size={20} />
                </div>
              </div>
            </a>
            <div className="relative">
              <CaptionWithInlineMore
                text={comments[selectedComment].post.message}
              />
            </div>
          </div>
        ) : (
          <div className="relative w-full group">
            <a
              href={linkToPost}
              target="_blank"
              rel="noopener noreferrer"
              className="block relative"
            >
              <div className="h-20 mb-1 text-white text-base font-medium line-clamp-4 text-center bg-gradient-to-b from-gray-800 to-black p-3 flex items-center justify-center rounded overflow-hidden">
                <p
                  ref={captionRef}
                  className="transform transition-all duration-300 group-hover:scale-105"
                  style={{ overflow: 'hidden' }}
                >
                  {comments[selectedComment].post.message}
                </p>
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                  <BoxArrowUpRight className="text-white" size={20} />
                </div>
                {isCaptionLong && (
                  <button
                    className="absolute right-2 bottom-2 text-xs text-blue-500 bg-white bg-opacity-80 px-1 rounded hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowCaptionOverlay(true);
                    }}
                  >
                    More
                  </button>
                )}
              </div>
            </a>
          </div>
        )}
      </div>
      <CommentDetailed
        isLoading={isLoading}
        comments={comments}
        selectedComment={selectedComment}
        deleteWindow={deleteWindow}
        updateCommentHiddenStatus={updateCommentHiddenStatus}
        isCommentHidden={isCommentHidden}
        setReplyToggle={setReplyToggle}
        autoMod={autoMod}
        currentSocial={currentSocial}
      />
      <CommentReplyPane
        isLoading={isLoading}
        currentSocial={currentSocial}
        comments={comments}
        setComments={setComments}
        selectedComment={selectedComment}
        setSelectedComment={setSelectedComment}
        replyToComment={replyToComment}
        isCommentHidden={isCommentHidden}
        updateCommentHiddenStatus={updateCommentHiddenStatus}
      />
      {isCommentHidden && !autoMod && (
        <div>
          <div className="flex flex-col space-y-2 px-2 py-2">
            <div className="flex items-center text-white text-opacity-60 text-xs"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const CommentDetailed = ({
  isLoading,
  comments,
  selectedComment,
  deleteWindow,
  updateCommentHiddenStatus,
  isCommentHidden,
  setReplyToggle,
  autoMod,
  currentSocial,
}: {
  comments: UnifiedCommentDto[];
  selectedComment: any;
  deleteWindow: any;
  isLoading: boolean;
  updateCommentHiddenStatus: any;
  isCommentHidden: boolean;
  setReplyToggle: any;
  autoMod: any;
  currentSocial: Social;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  // Translation state
  const [showTranslateMenu, setShowTranslateMenu] = useState(false);
  const [translatedData, setTranslatedData] =
    useState<TranslatedCommentDto | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(
        textRef.current.scrollHeight > textRef.current.clientHeight
      );
    }
    console.log('selectedComment:', selectedComment);

    setTranslatedData(null);
    setShowOriginal(false);
    setTranslationError(null);
    setShowTranslateMenu(false);
  }, [selectedComment, comments]);

  const handleTranslate = async (targetLanguage: SupportedLanguage) => {
    if (!comments[selectedComment]) return;

    setIsTranslating(true);
    setTranslationError(null);
    setShowTranslateMenu(false);

    try {
      const result = await translateComment(
        comments[selectedComment].platformCommentId,
        currentSocial.id,
        targetLanguage
      );

      if (result.success) {
        setTranslatedData(result);
        setShowOriginal(false);
      } else {
        setTranslationError(result.error || 'Translation failed');
      }
    } catch (error) {
      setTranslationError(
        error instanceof Error ? error.message : 'Translation failed'
      );
    } finally {
      setIsTranslating(false);
    }
  };

  // Toggle between original and translated text
  const toggleTranslation = () => {
    setShowOriginal(!showOriginal);
  };

  if (selectedComment === null || comments[selectedComment] === undefined) {
    return <></>;
  }

  const hasReply =
    selectedComment !== null &&
    selectedComment !== undefined &&
    comments[selectedComment]?.status === 2;

  const { username, message, platformCreatedAt, hidden, moderationReason } =
    comments[selectedComment];
  const mediaUrl = comments[selectedComment].attachmentUrl || '';
  const linkToPost = comments[selectedComment].post?.permalinkUrl || '#';

  const displayText =
    translatedData && !showOriginal ? translatedData.translatedText : message;

  return (
    <div className="flex flex-col px-2 py-2 border-b border-opacity-20 border-white">
      <div className="flex flex-col">
        <div className="pb-1 flex flex-row text-xs items-center justify-between">
          <p className="line-clamp-1">@{username}</p>
          <a
            href={linkToPost}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-blue-500 hover:text-blue-700"
          >
            {formatDate(platformCreatedAt)}
          </a>
        </div>
        <div className="flex items-center pt-1 space-x-1">
          <span
            className={`text-xs font-semibold ${
              hidden ? 'text-orange' : 'text-green-400'
            }`}
          >
            {hidden ? 'Hidden' : 'Not Hidden'}
          </span>
          <div className="relative group flex items-center">
            <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
            {moderationReason && (
              <div className="absolute left-6 top-1/2 -translate-y-1/2 z-20 hidden group-hover:block bg-black bg-opacity-90 text-white text-xs rounded px-4 py-2 shadow-lg whitespace-pre-line max-w-md min-w-[200px]">
                {moderationReason.split('|')[0]}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="h-16 relative overflow-hidden">
            <div
              className={`w-full ${
                isExpanded ? 'static' : 'absolute'
              } overflow-hidden transition-all duration-300 ${
                isExpanded ? 'h-auto' : 'h-16'
              }`}
            >
              {mediaUrl && (
                <div
                  className={`${
                    !displayText || displayText.length < 30
                      ? 'flex justify-center py-1'
                      : ''
                  }`}
                >
                  <img
                    className={`${
                      displayText && displayText.length >= 30
                        ? 'float-right'
                        : ''
                    } ml-2 mb-1 max-h-14 max-w-[30%] object-contain cursor-pointer rounded-md`}
                    src={mediaUrl}
                    alt="Image with comment"
                    onClick={() => setIsImageExpanded(true)}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      if (e.currentTarget.parentElement) {
                        e.currentTarget.parentElement.innerHTML +=
                          '<p class="text-gray-400 text-xs">Image no longer available</p>';
                      }
                    }}
                  />
                </div>
              )}
              {displayText && (
                <div className="max-h-16 overflow-y-auto pr-2">
                  <p ref={textRef} className="text-sm">
                    {displayText}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Translation controls */}
          <div className="mt-2 space-y-1">
            {/* Original/Translated toggle and translation info */}
            {translatedData && (
              <div className="flex items-center justify-between">
                <button
                  className="text-xs text-blue-500 hover:text-blue-700 underline"
                  onClick={toggleTranslation}
                >
                  {showOriginal ? 'Show Translation' : 'See Original'}
                </button>
                {!showOriginal && (
                  <span className="text-xs text-gray-400">
                    Translated from {translatedData.sourceLanguage} to{' '}
                    {LanguageDisplayNames[translatedData.targetLanguage]}
                  </span>
                )}
              </div>
            )}

            {/* Translation error */}
            {translationError && (
              <div className="text-xs text-red-500 bg-red-500 bg-opacity-10 rounded px-2 py-1">
                {translationError}
              </div>
            )}
          </div>

          {isOverflowing && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-blue-500 hover:text-blue-700 text-xs mt-1"
            >
              {isExpanded ? 'Read less' : 'Read more'}
            </button>
          )}

          {isImageExpanded && mediaUrl && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
              onClick={() => setIsImageExpanded(false)}
            >
              <div className="relative max-w-3xl max-h-[80vh]">
                <img
                  src={mediaUrl}
                  alt="Expanded image"
                  className="max-w-full max-h-full object-contain rounded-md"
                />
                <button
                  className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsImageExpanded(false);
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex pt-1 flex-wrap gap-1">
          <button
            className="text-xs flex items-center bg-white bg-opacity-10 hover:bg-white hover:bg-opacity-20 rounded-xl py-1 px-2"
            onClick={() => updateCommentHiddenStatus()}
          >
            {!isCommentHidden ? (
              <>
                <EyeIcon className="text-white w-4 h-4 cursor-pointer" />
                <span className="ml-1">Hide</span>
              </>
            ) : (
              <>
                <EyeSlashIcon className="text-white w-4 h-4 cursor-pointer" />
                <span className="ml-1">Unhide</span>
              </>
            )}
          </button>

          <button
            className="text-red text-xs bg-red bg-opacity-10 flex hover:bg-red hover:bg-opacity-20 rounded-xl py-1 px-2 items-center"
            onClick={() => deleteWindow()}
          >
            <TrashIcon className="w-4 h-4 cursor-pointer" />
            <span className="ml-1">Delete</span>
          </button>

          {/* Translation button */}
          {!translatedData && (
            <div className="relative">
              <button
                className="text-xs flex items-center bg-purple-300 bg-opacity-10 hover:bg-purple-500 hover:bg-opacity-20 rounded-xl py-1 px-2 disabled:opacity-50"
                onClick={() => setShowTranslateMenu(!showTranslateMenu)}
                disabled={isTranslating}
              >
                <LanguageIcon className="w-4 h-4 text-purple-500" />
                <span className="ml-1 text-purple-200">
                  {isTranslating ? 'Translating...' : 'Translate'}
                </span>
                <ChevronDownIcon className="w-3 h-3 ml-1 text-purple-200" />
              </button>

              {/* Language selection dropdown */}
              {showTranslateMenu && (
                <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-30 min-w-[160px]">
                  {Object.entries(LanguageDisplayNames).map(([code, name]) => (
                    <button
                      key={code}
                      className="block w-full text-left px-3 py-2 text-xs text-white hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                      onClick={() => handleTranslate(code as SupportedLanguage)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {autoMod && (
            <button
              className={`text-xs bg-green-500 bg-opacity-10 flex hover:bg-green hover:bg-opacity-20 rounded-xl py-1 px-2 items-center
              ${hasReply ? 'cursor-not-allowed' : ''}`}
              onClick={() => setReplyToggle((prev: any) => !prev)}
            >
              <Send className="w-4 h-4 cursor-pointer text-green-500" />
              <span className="ml-1 text-green-500">Reply</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedPane;
